// lazy() that survives a new deploy (24 Oct 2026).
//
// Every deploy gives each page's chunk a new file name and removes the old ones. A tab opened
// before the deploy still asks for the old name, Cloudflare answers with the app's index.html
// (single-page fallback), the import fails, and with nothing to catch it React unmounts the
// whole app: a blank page until the reader refreshes. Here a failed chunk load reloads the page
// once instead, which fetches the new file names. The guard stops a loop if the chunk is
// genuinely broken: a second failure within a minute is thrown as before.
import { lazy as reactLazy, type ComponentType } from "react";

const KEY = "sn-chunk-reload";
let pending = false;

export function reloadOnceForNewDeploy(): boolean {
  if (pending) return true;
  try {
    const last = Number(sessionStorage.getItem(KEY) || 0);
    if (Date.now() - last < 60_000) return false;
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    // sessionStorage blocked: reload anyway, the browser's own history stops a tight loop
  }
  pending = true;
  window.location.reload();
  return true;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazy<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return reactLazy(() =>
    load().catch((err) => {
      if (reloadOnceForNewDeploy()) return new Promise<{ default: T }>(() => {});
      throw err;
    }),
  );
}
